import { metadataApi } from '@platform/api';
import { MetadataTypes, ProjectTypes } from '@platform/types';
import { GetVariableUniverse, ValueFormatter } from '@platform/utils';
import { scale } from 'chroma-js';
import { v4 as uuidv4 } from 'uuid';
import * as peApi from '../../../../pe.api';

const INSUFFICIENT_DATA_CUT_POINT = { label: 'No data', color: '#C0C0C0' };

const COLOR_PALETTES_PER_VISUALIZATION_TYPE = {
  [ProjectTypes.VisualizationType.SHADED]: {
    single: ['polygon-sequential', 'polygon-diverging'],
    multi: ['MULTI-POLYGON'],
  },
  [ProjectTypes.VisualizationType.BUBBLES]: { single: ['bubble-single-color'], multi: [] },
  [ProjectTypes.VisualizationType.DOT_DENSITY]: { single: [], multi: [] },
};

const FALLBACK_PERCENT_BASED_CUT_POINT_SET: ProjectTypes.CutPointSet = {
  insufficientData: INSUFFICIENT_DATA_CUT_POINT,
  cutPoints: [
    { title: '< 5%', displayInLegend: true, from: -Number.MAX_VALUE, to: 5 },
    { title: '5% to 10%', displayInLegend: true, from: 5, to: 10 },
    { title: '10% to 25%', displayInLegend: true, from: 10, to: 25 },
    { title: '25% to 40%', displayInLegend: true, from: 25, to: 40 },
    { title: '40% to 60%', displayInLegend: true, from: 40, to: 60 },
    { title: '60% to 75%', displayInLegend: true, from: 60, to: 75 },
    { title: '75% to 90%', displayInLegend: true, from: 75, to: 90 },
    { title: '> 90%', displayInLegend: true, from: 90, to: Number.MAX_VALUE },
  ],
};

const FALLBACK_COUNT_BASED_CUT_POINT_SET: ProjectTypes.CutPointSet = {
  insufficientData: INSUFFICIENT_DATA_CUT_POINT,
  cutPoints: [
    { title: '< 50', displayInLegend: true, from: Number.MIN_VALUE, to: 50 },
    { title: '50 to 500', displayInLegend: true, from: 50, to: 500 },
    { title: '500 to 1,000', displayInLegend: true, from: 500, to: 1000 },
    { title: '1,000 to 3,000', displayInLegend: true, from: 1000, to: 3000 },
    { title: '3,000 to 5,000', displayInLegend: true, from: 3000, to: 5000 },
    { title: '5,000 to 7,000', displayInLegend: true, from: 5000, to: 7000 },
    { title: '7,000 to 15,000', displayInLegend: true, from: 7000, to: 15000 },
    { title: '> 15,000', displayInLegend: true, from: 15000, to: Number.MAX_VALUE },
  ],
};

export const getFormatting = (
  format: ProjectTypes.ValueFormat,
  options?: {
    currency?: 'USD' | 'EUR' | 'JPY';
  }
): string => {
  // TODO: complete the mapping
  switch (format) {
    case ProjectTypes.ValueFormat.FORMAT_PERCENT_NO_DECIMAL:
      return '0%';
    case ProjectTypes.ValueFormat.FORMAT_PERCENT_1_DECIMAL:
      return '0.0%';
    case ProjectTypes.ValueFormat.FORMAT_NUMBER_NO_DECIMAL:
      return '0,0';
    case ProjectTypes.ValueFormat.FORMAT_NUMBER_1_DECIMAL:
      return '0,0.0';
    case ProjectTypes.ValueFormat.FORMAT_NUMBER_2_DECIMAL:
      return '0,0.00';
    case ProjectTypes.ValueFormat.FORMAT_NUMBER:
      return '0,0';
    case ProjectTypes.ValueFormat.FORMAT_CURRENCY:
      return `${options?.currency}0,0[.]00`;
  }
  return '';
};

interface ShadedRenderingInput {
  surveyName: string;
  datasetAbbreviation: string;
  variableSelection: ProjectTypes.WorkingLayerVariableSelection[];
  currentGeoLayerId?: string;
  id?: string;
}

export const getShadedWorkingLayer = async ({
  id,
  surveyName,
  variableSelection,
  datasetAbbreviation,
  currentGeoLayerId,
}: ShadedRenderingInput) => {
  const [firstVariable] = variableSelection;
  const { tableId } = firstVariable;
  const [surveyMetadata] = await metadataApi.getSurveys([surveyName]);
  const tableMetadata = await metadataApi.loadTable(surveyName, datasetAbbreviation, tableId);
  const filterSets = await metadataApi.getSystemFilterSets(surveyName, datasetAbbreviation);
  const palettes = await peApi.getColorPalettes(surveyName);
  const dataSourceLayers = await peApi.getDataSourceLayers(surveyName, surveyMetadata.isUserSurvey);

  const preferredDataSourceLayer =
    dataSourceLayers.find((x) => x['geo-layer-id'] === currentGeoLayerId) ?? dataSourceLayers[0];

  const geoLayerId = preferredDataSourceLayer['geo-layer-id'];
  const dataset = preferredDataSourceLayer.datasets.find((ds) => ds['dataset-abbreviation'] === datasetAbbreviation);

  if (!dataset) throw new Error('DATASET NOT FOUND!!!!!'); // TODO:
  const colorPalette = {
    id: 'PE',
    type: 'sequential',
  };
  const newDataTheme = await figureOutDataTheme(
    variableSelection.map((variable) => ({
      surveyName,
      isUserSurvey: !!surveyMetadata.isUserSurvey,
      datasetAbbrev: datasetAbbreviation,
      variableGuid: variable.variableId,
      tableGuid: variable.tableId,
      universeVariableUuid: variable.universeVariableId,
    })),
    palettes,
    filterSets,
    colorPalette
  );

  if (!newDataTheme) throw new Error('CANT CREATE DATA THEME!!!!!'); // TODO:

  const shadedRenderingStyle = {
    colorPalette,
    cutPointSets: newDataTheme.rendering,
    opacities: [1, 0.4],
  };

  const dataLayerId = dataset['dataset-id'];

  const workingLayer: ProjectTypes.SocialExplorerWorkingLayer = {
    id: id ?? uuidv4(),
    name: tableMetadata.titleShort,
    description: surveyMetadata.displayName,
    rendering: { type: 'shaded', style: shadedRenderingStyle },
    source: 'social-explorer',
    type: ProjectTypes.LayerType.POLYGON,
    visible: true,
    featureSelection: [],
    maskingSelection: {
      doMask: false,
      features: [],
    },
    variableSelection: [...variableSelection],
    metadata: {
      tiles: `${process.env.NX_SOCEX_TILES_SERVER}?x={x}&y={y}&z={z}&layers={layers}&columns={columns}&projection=EPSG-3857`,
      geoLayerId,
      dataLayerId,
      identifiers: {
        row: {
          variableId: dataset['primary-key-column'] ?? '0', // TODO: check what if undefined 0?
          datasetId: dataLayerId,
        },
        name: {
          variableId: dataset['geo-qualified-name-column'] ?? '0', // TODO: check what if undefined 0?
          datasetId: dataLayerId,
        },
      },
      surveyName: surveyName,
      datasetAbbreviation: datasetAbbreviation,
      isUserSurvey: surveyMetadata.isUserSurvey,
    },
    tooltip: {
      action: 'click',
      enabled: true,
      title: dataset['geo-qualified-name-column']
        ? {
            id: 'title',
            label: '',
            variableId: dataset['geo-qualified-name-column'],
          }
        : undefined,
      content: variableSelection.map((variable) => {
        const variableMetadata = tableMetadata.variables.find(
          (x) => x.uuid === variable.variableId
        ) as MetadataTypes.Variable;

        const universe = GetVariableUniverse(variable.variableId, tableMetadata);

        let pctBase: ProjectTypes.TooltipLineItem | undefined = undefined;

        if (universe.length) {
          const [firstUniverse] = universe;
          pctBase = {
            id: uuidv4(),
            variableId: firstUniverse.uuid,
            label: firstUniverse.label,
            format: getFormatting(firstUniverse.formatting as ProjectTypes.ValueFormat),
          };
        }

        return {
          comparisonBase: pctBase ? { type: 'pct', base: pctBase } : undefined,
          format: pctBase ? '0,0.00%' : getFormatting(variableMetadata.formatting as ProjectTypes.ValueFormat),
          id: uuidv4(),
          variableId: variable.variableId,
          label: variableMetadata.label,
        };
      }),
    },
  };

  return workingLayer;
};

export const interpolateColors = (colorPalette: MetadataTypes.ColorPalette, count: number): string[] => {
  const { colors, type, ['color-ramps']: colorRamps } = colorPalette;
  if (colors.length > 0) {
    const colorsToUse: string[] = [];
    for (let i = 0; i < count; i += 1) {
      colors.push(colorsToUse[i % colors.length]);
    }
    return colorsToUse;
  }

  if (colorRamps.length > 0) {
    let bias = 0;
    const colorsInRamp: string[] = [];
    const domain: number[] = [];

    switch (type) {
      case 'polygon-diverging': {
        // TODO: needs more tweaking for sure
        const firstRamp = colorRamps[0];
        const lastRamp = colorRamps[colorRamps.length - 1];
        colorsInRamp.push(firstRamp.from, firstRamp.to);
        colorsInRamp.push(lastRamp.from, lastRamp.to);
        domain.push(0);
        domain.push(1);
        break;
      }
      case 'polygon-sequential':
      default: {
        colorRamps.forEach((colorRamp, colorRampIndex) => {
          colorsInRamp.push(colorRamp.from, colorRamp.to);
          const rampBias = colorRamp.bias === 0 ? (colorRampIndex + 1) / colorRamps.length : colorRamp.bias;
          domain.push(bias, bias + rampBias);
          bias += colorRamp.bias;
        });
      }
    }
    return scale(colorsInRamp).domain(domain).colors(count);
  }
  return [];
};

const figureOutDataTheme = async (
  selection: ProjectTypes.VariableSelection[],
  colorPalettes: MetadataTypes.ColorPalette[],
  systemCategoryFilters: MetadataTypes.SystemCategoryFilters[],
  defaultColorPalette?: {
    id: string;
    type: string;
  }
): Promise<
  | {
      type: ProjectTypes.VisualizationType;
      colorPalette: {
        id: string;
        type: string;
      };
      rendering: ProjectTypes.CutPointSet[];
      percentBasedVisualization: boolean;
    }
  | undefined
> => {
  const [firstSelectedVariable] = selection;
  const { variableMetadata, tableMetadata } = await getSelectionMetadata(firstSelectedVariable);
  if (!tableMetadata || !variableMetadata) {
    console.warn('metadata not available. Exiting...');
    return;
  }

  const percentBasedVisualization =
    variableMetadata.dataType !== 'String' && firstSelectedVariable.universeVariableUuid != null;

  const type = ProjectTypes.VisualizationType.SHADED;

  const applicableColorPalettes = getApplicableColorPalettes({
    colorPalettes,
    isMultiSelection: selection.length > 1,
    visualizationType: type,
  });

  // use the default palette
  let colorPaletteToUse = applicableColorPalettes.find(
    (cp) => cp.id === defaultColorPalette?.id && cp.type === defaultColorPalette?.type
  );

  // but check if the selected variable requires a palette specified by its metadata
  if (variableMetadata.suggestedPaletteName && variableMetadata.suggestedPaletteType) {
    colorPaletteToUse = applicableColorPalettes.find(
      (cp) =>
        cp.id === variableMetadata.suggestedPaletteName &&
        cp.type.toLowerCase() === `polygon-${variableMetadata.suggestedPaletteType}`.toLowerCase()
    );
  }

  if (!colorPaletteToUse) {
    colorPaletteToUse = applicableColorPalettes[0];
  }

  const suggestedFilterSetName = variableMetadata.defaultFilterSetName || tableMetadata.defaultFilterSetName;

  let cutPointSetToUse = percentBasedVisualization
    ? FALLBACK_PERCENT_BASED_CUT_POINT_SET
    : FALLBACK_COUNT_BASED_CUT_POINT_SET;

  if (suggestedFilterSetName) {
    const suggestedFilterSets = systemCategoryFilters.find((x) => x.name === suggestedFilterSetName);
    if (suggestedFilterSets?.filterSets?.length) {
      if (variableMetadata.dataType === 'String' || variableMetadata.varType === 'None') {
        cutPointSetToUse = {
          insufficientData: INSUFFICIENT_DATA_CUT_POINT,
          cutPoints: suggestedFilterSets.filterSets[0].filters.map((f) => ({
            title: f.label || f.valueStr || f.valueNum || 'N/A',
            to: f.valueStr || f.valueNum,
            from: f.valueStr || f.valueNum,
            displayInLegend: true,
          })),
        };
      } else if (variableMetadata.indent > 0) {
        const filterSetToUse =
          suggestedFilterSets?.filterSets.find((f) => f.filters.length >= 8) ?? suggestedFilterSets.filterSets[0];

        if (filterSetToUse) {
          cutPointSetToUse = {
            insufficientData: INSUFFICIENT_DATA_CUT_POINT,
            cutPoints: filterSetToUse.filters.map((f, idx) => {
              let title;
              if (idx === 0) {
                // first
                title = `< ${f.to}%`;
              } else if (idx === filterSetToUse.filters.length - 1) {
                // last
                title = `> ${f.from}%`;
              } else {
                // somewhere in the middle
                title = `${f.from}% to ${f.to}%`;
              }

              return {
                title,
                to: f.to,
                from: f.from,
                displayInLegend: true,
              };
            }),
          };
        }
      }
    }
  }

  const rendering = getRenderingRules(colorPaletteToUse, cutPointSetToUse, selection);

  return {
    type,
    rendering,
    colorPalette: { id: colorPaletteToUse.id, type: colorPaletteToUse.type },
    percentBasedVisualization,
  };
};

const getRenderingRules = (
  colorPalette: MetadataTypes.ColorPalette,
  cutPointSet: ProjectTypes.CutPointSet,
  selection: ProjectTypes.VariableSelection[],
  defaultRenderingOptions?: ProjectTypes.CutPointSet[]
): ProjectTypes.CutPointSet[] => {
  const renderingOptions: ProjectTypes.CutPointSet[] =
    defaultRenderingOptions ?? Array(selection.length).fill(cutPointSet);

  const renderingRules: ProjectTypes.CutPointSet[] = [];

  const isMultiVariable = selection.length > 1;

  if (isMultiVariable) {
    selection.forEach((_, idx) => {
      const selectionRendering = renderingOptions[idx];
      const cRamp = colorPalette['color-ramps'][idx];
      const colorsToApply = scale([cRamp.from, cRamp.to])
        .domain([cRamp.bias, 1])
        .colors(selectionRendering.cutPoints.length + 2);

      const cutPoints: ProjectTypes.CutPoint[] = selectionRendering.cutPoints.map((cp, idx) => ({
        ...cp,
        color: colorsToApply[idx],
      }));

      renderingRules.push({ ...selectionRendering, cutPoints });
    });
  } else {
    const cutPoints: ProjectTypes.CutPoint[] = [];
    const colorsToApply = interpolateColors(colorPalette, renderingOptions[0].cutPoints.length);

    cutPoints.push(...renderingOptions[0].cutPoints.map((cp, idx) => ({ ...cp, color: colorsToApply[idx] })));
    renderingRules.push({ ...renderingOptions[0], cutPoints });
  }

  return renderingRules;
};

const getSelectionMetadata = async (selection: ProjectTypes.VariableSelection) => {
  try {
    const tableMetadata = await metadataApi.loadTable(
      selection.surveyName,
      selection.datasetAbbrev,
      selection.tableGuid
    );
    if (!tableMetadata) return {};

    const variableMetadata = tableMetadata.variables.find((x) => x.uuid === selection.variableGuid);
    if (!variableMetadata) return {};

    return { tableMetadata, variableMetadata };
  } catch (error) {
    console.warn('cant load meta', error);
    return {};
  }
};

const getApplicableColorPalettes = ({
  colorPalettes,
  isMultiSelection,
  visualizationType,
}: {
  colorPalettes: MetadataTypes.ColorPalette[];
  isMultiSelection: boolean;
  visualizationType: ProjectTypes.VisualizationType;
}) => {
  const retVal: MetadataTypes.ColorPalette[] = [];
  COLOR_PALETTES_PER_VISUALIZATION_TYPE[visualizationType][isMultiSelection ? 'multi' : 'single'].forEach(
    (colorPaletteType) => {
      retVal.push(...colorPalettes.filter((cp) => cp.type === colorPaletteType));
    }
  );
  return retVal;
};

export const figureOutCutPoints = async (
  selection: ProjectTypes.VariableSelection[],
  steps: number[],
  colorPalette: MetadataTypes.ColorPalette
): Promise<ProjectTypes.CutPointSet[] | undefined> => {
  const [firstSelectedVariable] = selection;
  const { variableMetadata } = await getSelectionMetadata(firstSelectedVariable);

  if (!variableMetadata) {
    console.warn('metadata not available. Exiting...');
    return;
  }

  const valueFormat = selection[0].universeVariableUuid
    ? ProjectTypes.ValueFormat.FORMAT_PERCENT_1_DECIMAL
    : (variableMetadata.formatting as ProjectTypes.ValueFormat);

  const cutPointSetToUse: ProjectTypes.CutPointSet = {
    insufficientData: INSUFFICIENT_DATA_CUT_POINT,
    cutPoints: steps.map((step, idx) => {
      let title, to, from;
      if (idx === 0) {
        // first
        from = -Number.MAX_VALUE;
        to = step;
        title = `< ${ValueFormatter.format(to, valueFormat)}`;
      } else if (idx === steps.length - 1) {
        // last
        from = step;
        to = Number.MAX_VALUE;
        title = `> ${ValueFormatter.format(from, valueFormat)}`;
      } else {
        // somewhere in the middle
        from = step;
        to = steps[idx + 1];
        title = `${ValueFormatter.format(from, valueFormat)} to ${ValueFormatter.format(to, valueFormat)}`;
      }

      return {
        title,
        to,
        from,
        displayInLegend: true,
      };
    }),
  };

  return getRenderingRules(colorPalette, cutPointSetToUse, selection);
};
